import {
	Activity,
	BarChart3,
	Box,
	Building2,
	FileText,
	Home,
	LayoutTemplate,
	LogOut,
	Menu,
	Settings,
	Shield,
	Users,
	X,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import apiClient from "../../api/client";
import RoleBadge from "../../components/RoleBadge";
import { useAuthStore } from "../../store/authStore";
import ActivityLogs from "./ActivityLogs";
import ClientDetail from "./ClientDetail";
import Clients from "./Clients";
import Companies from "./Companies";
// Import pages
import DashboardHome from "./DashboardHome";
import Modules from "./Modules";
import Plans from "./Plans";
import ProposalDetail from "./ProposalDetail";
import ProposalNew from "./ProposalNew";
import Proposals from "./Proposals";
import SettingsPage from "./Settings";
import TemplateEditor from "./TemplateEditor";
import Templates from "./Templates";
import UsersPage from "./Users";

export default function AdminDashboard() {
	const navigate = useNavigate();
	const location = useLocation();
	const { logout, user, hasPermission } = useAuthStore();
	const [sidebarOpen, setSidebarOpen] = useState(true);
	const [appSettings, setAppSettings] = useState({
		app_name: "Proposta",
		app_logo_url: "",
	});

	const refreshAppSettings = React.useCallback(() => {
		apiClient
			.get("/settings/public")
			.then((res) => {
				setAppSettings({
					app_name: res.data.app_name || "Proposta",
					app_logo_url: res.data.app_logo_url || "",
				});
			})
			.catch(() => {});
	}, []);

	useEffect(() => {
		refreshAppSettings();
	}, [refreshAppSettings]);

	const handleLogout = () => {
		logout();
		navigate("/admin/login");
	};

	const allNavItems = [
		{ path: "/admin", label: "Dashboard", icon: Home, roles: null },
		{
			path: "/admin/companies",
			label: "Empresas",
			icon: Building2,
			roles: null,
		},
		{ path: "/admin/plans", label: "Planos", icon: BarChart3, roles: null },
		{ path: "/admin/modules", label: "Modulos", icon: Box, roles: null },
		{ path: "/admin/clients", label: "Clientes", icon: Users, roles: null },
		{
			path: "/admin/proposals",
			label: "Propostas",
			icon: FileText,
			roles: null,
		},
		{
			path: "/admin/templates",
			label: "Templates",
			icon: LayoutTemplate,
			roles: null,
		},
		{
			path: "/admin/activity-logs",
			label: "Logs",
			icon: Activity,
			roles: ["admin", "manager"],
		},
		{ path: "/admin/users", label: "Usuarios", icon: Shield, roles: ["admin"] },
		{
			path: "/admin/settings",
			label: "Configuracoes",
			icon: Settings,
			roles: ["admin"],
		},
	];

	const navItems = allNavItems.filter(
		(item) => !item.roles || hasPermission(...item.roles),
	);

	const isActive = (path) => {
		if (path === "/admin") {
			return location.pathname === path;
		}
		return location.pathname.startsWith(path);
	};

	return (
		<div className="min-h-screen bg-[#0B0F19] text-slate-300">
			{/* Sidebar */}
			<aside
				className={`fixed left-0 top-0 h-screen bg-slate-900/50 backdrop-blur-xl border-r border-white/5 transition-all ${
					sidebarOpen ? "w-64" : "w-0 md:w-20"
				} overflow-hidden z-40`}
			>
				<div className="p-6 flex items-center justify-between">
					{sidebarOpen && (
						<>
							<div className="flex items-center gap-2 min-w-0">
								{appSettings.app_logo_url ? (
									<img
										src={appSettings.app_logo_url}
										alt={appSettings.app_name}
										className="h-8 object-contain flex-shrink-0"
									/>
								) : (
									<h1 className="text-xl font-bold text-cyan-400 truncate">
										{appSettings.app_name}
									</h1>
								)}
							</div>
							<button
								type="button"
								onClick={() => setSidebarOpen(false)}
								className="md:hidden text-slate-400 hover:text-white transition flex-shrink-0"
							>
								<X className="w-5 h-5" />
							</button>
						</>
					)}
				</div>

				<nav className="mt-8 px-4 space-y-2">
					{navItems.map(({ path, label, icon: Icon }) => (
						<button
							key={path}
							type="button"
							onClick={() => navigate(path)}
							className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition ${
								isActive(path)
									? "bg-cyan-600/20 text-cyan-400 border border-cyan-500/30"
									: "text-slate-400 hover:text-slate-300 hover:bg-slate-800/30"
							}`}
							title={sidebarOpen ? "" : label}
						>
							<Icon className="w-5 h-5 flex-shrink-0" />
							{sidebarOpen && (
								<span className="text-sm font-medium">{label}</span>
							)}
						</button>
					))}
				</nav>

				<div className="absolute bottom-6 left-4 right-4 space-y-2">
					<div
						className={`p-3 rounded-lg bg-slate-800/30 border border-slate-700/50 ${!sidebarOpen && "hidden"}`}
					>
						<p className="text-xs text-slate-500">Logado como</p>
						<p className="text-sm font-medium text-slate-300 truncate">
							{user?.name || user?.email}
						</p>
						{user?.role && (
							<div className="mt-1">
								<RoleBadge role={user.role} size="xs" />
							</div>
						)}
					</div>
					<button
						type="button"
						onClick={handleLogout}
						className="w-full flex items-center gap-2 px-4 py-2 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition text-sm font-medium"
						title={sidebarOpen ? "" : "Logout"}
					>
						<LogOut className="w-5 h-5" />
						{sidebarOpen && "Sair"}
					</button>
				</div>
			</aside>

			{/* Main Content */}
			<main
				className={`transition-all ${sidebarOpen ? "md:ml-64" : "md:ml-20"}`}
			>
				{/* Top Bar */}
				<header className="sticky top-0 bg-slate-900/50 backdrop-blur-xl border-b border-white/5 px-6 py-4 flex items-center justify-between z-30">
					<button
						type="button"
						onClick={() => setSidebarOpen(!sidebarOpen)}
						className="text-slate-400 hover:text-white transition"
					>
						<Menu className="w-6 h-6" />
					</button>
					<h2 className="text-lg font-semibold text-white">
						{navItems.find((item) => isActive(item.path))?.label || "Dashboard"}
					</h2>
					<div className="w-6" />
				</header>

				{/* Content Area */}
				<div className="p-6">
					<Routes>
						<Route path="/" element={<DashboardHome />} />
						<Route path="/companies" element={<Companies />} />
						<Route path="/plans" element={<Plans />} />
						<Route path="/modules" element={<Modules />} />
						<Route path="/clients" element={<Clients />} />
						<Route path="/clients/:id" element={<ClientDetail />} />
						<Route path="/proposals" element={<Proposals />} />
						<Route path="/proposals/new" element={<ProposalNew />} />
						<Route path="/proposals/:id" element={<ProposalDetail />} />
						<Route path="/templates" element={<Templates />} />
						<Route path="/templates/new" element={<TemplateEditor />} />
						<Route
							path="/settings"
							element={<SettingsPage onSettingsSaved={refreshAppSettings} />}
						/>
						<Route path="/users" element={<UsersPage />} />
						<Route path="/activity-logs" element={<ActivityLogs />} />
					</Routes>
				</div>
			</main>
		</div>
	);
}
